import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { FScore } from '../../types';
import { formatPercentage } from '../../utils/formatters';

interface FScoreBreakdownProps {
  fScore: FScore;
}

interface FScoreChartItem {
  name: string;
  value: number;
  criteria: string;
}

const FScoreBreakdown: React.FC<FScoreBreakdownProps> = ({ fScore }) => {
  const getFScoreChartData = () => {
    return [
      { name: 'ROA', value: fScore.roa ? 1 : 0, criteria: 'Return on Assets is positive' },
      { name: 'CFO', value: fScore.cfo ? 1 : 0, criteria: 'Cash Flow from Operations is positive' },
      { name: 'ROA Δ', value: fScore.deltaRoa ? 1 : 0, criteria: 'Return on Assets is higher than previous year' },
      { name: 'Accruals', value: fScore.accrual ? 1 : 0, criteria: 'Cash Flow from Operations > Net Income (Lower Accruals)' },
      { name: 'Leverage Δ', value: fScore.deltaLeverage ? 1 : 0, criteria: 'Long-term Debt to Assets ratio has decreased' },
      { name: 'Liquidity Δ', value: fScore.deltaLiquidity ? 1 : 0, criteria: 'Current Ratio has improved' },
      { name: 'No Dilution', value: fScore.noEquityRaise ? 1 : 0, criteria: 'No new shares were issued' },
      { name: 'Margin Δ', value: fScore.deltaMargin ? 1 : 0, criteria: 'Gross Margin has improved' },
      { name: 'Turnover Δ', value: fScore.deltaTurnover ? 1 : 0, criteria: 'Asset Turnover ratio has improved' },
    ];
  };

  // Helper function to determine bar color
  const getBarColor = (entry: FScoreChartItem): string => {
    return entry.value === 1 ? '#10b981' : '#ef4444';
  };

  return (
    <div className="fScore-breakdown">
      <div className="fScore-summary mb-4">
        <div className="flex justify-between items-center px-4 py-3 bg-gray-50 rounded-lg">
          <span className="text-sm font-medium text-gray-700">Total F-Score:</span>
          <span className="text-lg font-semibold text-indigo-600">{fScore.score} / 9</span>
        </div>
        <div className="mt-2 text-sm text-gray-500">
          <p>A higher F-Score (closer to 9) indicates stronger financial health and potential for outperformance.</p>
        </div>
      </div>
      
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={getFScoreChartData()}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            layout="vertical"
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" domain={[0, 1]} ticks={[0, 1]} />
            <YAxis dataKey="name" type="category" width={80} />
            <Tooltip 
              formatter={(value: number) => [value === 1 ? 'Pass' : 'Fail', 'Result']} 
              labelFormatter={(label: string) => {
                const item = getFScoreChartData().find(item => item.name === label);
                return item ? item.criteria : label;
              }} 
            />
            <Bar 
              dataKey="value" 
              fill="#8884d8"
              name="Score" 
              isAnimationActive={true}
              barSize={20}
            >
              {
                getFScoreChartData().map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.value === 1 ? '#10b981' : '#ef4444'} />
                ))
              }
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-green-50 p-3 rounded border border-green-100">
          <h4 className="font-medium text-green-700">Profitability</h4>
          <ul className="mt-1 text-sm">
            <li className="flex items-center">
              <span className={`w-4 h-4 rounded-full mr-2 ${fScore.roa ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>ROA Positive</span>
            </li>
            <li className="flex items-center mt-1">
              <span className={`w-4 h-4 rounded-full mr-2 ${fScore.cfo ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>CFO Positive</span>
            </li>
            <li className="flex items-center mt-1">
              <span className={`w-4 h-4 rounded-full mr-2 ${fScore.deltaRoa ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>ROA Improvement</span>
            </li>
          </ul>
        </div>
        
        <div className="bg-blue-50 p-3 rounded border border-blue-100">
          <h4 className="font-medium text-blue-700">Financial Health</h4>
          <ul className="mt-1 text-sm">
            <li className="flex items-center">
              <span className={`w-4 h-4 rounded-full mr-2 ${fScore.accrual ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>Lower Accruals</span>
            </li>
            <li className="flex items-center mt-1">
              <span className={`w-4 h-4 rounded-full mr-2 ${fScore.deltaLeverage ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>Reduced Leverage</span>
            </li>
            <li className="flex items-center mt-1">
              <span className={`w-4 h-4 rounded-full mr-2 ${fScore.deltaLiquidity ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>Improved Liquidity</span>
            </li>
          </ul>
        </div>
        
        <div className="bg-purple-50 p-3 rounded border border-purple-100">
          <h4 className="font-medium text-purple-700">Operating Efficiency</h4>
          <ul className="mt-1 text-sm">
            <li className="flex items-center">
              <span className={`w-4 h-4 rounded-full mr-2 ${fScore.noEquityRaise ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>No Share Dilution</span>
            </li>
            <li className="flex items-center mt-1">
              <span className={`w-4 h-4 rounded-full mr-2 ${fScore.deltaMargin ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>Margin Improvement</span>
            </li>
            <li className="flex items-center mt-1">
              <span className={`w-4 h-4 rounded-full mr-2 ${fScore.deltaTurnover ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>Asset Turnover Growth</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FScoreBreakdown; 